<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="ReceiptsSkeleton">
    <!-- Desktop/tablet: table-shaped skeleton -->
    <LoaderSkeleton
      v-if="isTabletOrDesktop"
      class="ReceiptsSkeleton-table"
      :viewBox="tableViewBox"
    >
      <!-- Header labels -->
      <rect
        v-for="column of COLUMNS"
        :key="`header-${column.x}`"
        :x="column.x"
        :y="HEADER_LABEL_Y"
        :width="column.labelWidth"
        :height="LABEL_HEIGHT"
        rx="4"
        ry="4"
      />
      <!-- Status column header -->
      <rect :x="STATUS_X" :y="HEADER_LABEL_Y" :width="STATUS_LABEL_WIDTH" :height="LABEL_HEIGHT" rx="4" ry="4" />

      <!-- Body rows -->
      <template v-for="row of rowsLayout" :key="`row-${row.index}`">
        <rect
          v-for="column of COLUMNS"
          :key="`cell-${row.index}-${column.x}`"
          :x="column.x"
          :y="row.barY"
          :width="column.cellWidth"
          :height="CELL_HEIGHT"
          rx="4"
          ry="4"
        />
        <rect
          :x="STATUS_X"
          :y="row.badgeY"
          :width="STATUS_BADGE_WIDTH"
          :height="STATUS_BADGE_HEIGHT"
          rx="12"
          ry="12"
        />
      </template>

      <!-- Pagination: rows-per-page label + dropdown + prev + page + next -->
      <rect :x="PAGINATION_LABEL_X" :y="paginationCenterY - 6" rx="4" ry="4" width="105" height="12" />

      <rect :x="PAGINATION_DROPDOWN_X" :y="paginationCenterY - 15" rx="4" ry="4" width="64" height="29" />

      <rect :x="PAGINATION_PREV_X" :y="paginationCenterY - 10" rx="4" ry="4" width="20" height="20" />

      <rect :x="PAGINATION_PAGE_X" :y="paginationCenterY - 15" rx="4" ry="4" width="40" height="29" />

      <rect :x="PAGINATION_NEXT_X" :y="paginationCenterY - 10" rx="4" ry="4" width="20" height="20" />
    </LoaderSkeleton>

    <!-- Mobile: card-shaped skeletons -->
    <div v-else class="ReceiptsSkeleton-cards">
      <div
        v-for="index of rowsLayout"
        :key="`card-${index.index}`"
        class="ReceiptsSkeleton-card"
      >
        <LoaderSkeleton :viewBox="CARD_VIEW_BOX">
          <!-- Header: description + status tag -->
          <rect x="16" y="18" rx="4" ry="4" width="160" height="16" />
          <rect x="300" y="14" rx="12" ry="12" width="84" height="24" />
          <!-- Divider -->
          <rect x="16" y="52" width="368" height="1" />
          <!-- Field 1: label + value -->
          <rect x="16" y="72" rx="4" ry="4" width="40" height="10" />
          <rect x="16" y="88" rx="4" ry="4" width="80" height="14" />
          <!-- Field 2: label + value -->
          <rect x="200" y="72" rx="4" ry="4" width="56" height="10" />
          <rect x="200" y="88" rx="4" ry="4" width="80" height="14" />
          <!-- More details line -->
          <rect x="16" y="124" rx="4" ry="4" width="110" height="12" />
          <!-- CTAs: 2 buttons -->
          <rect x="16" y="160" rx="6" ry="6" width="176" height="40" />
          <rect x="208" y="160" rx="6" ry="6" width="176" height="40" />
        </LoaderSkeleton>
      </div>

      <!-- Mobile pagination skeleton -->
      <LoaderSkeleton class="ReceiptsSkeleton-pagination" viewBox="0 0 400 52">
        <!-- Rows per page label + dropdown -->
        <rect x="48" y="20" rx="4" ry="4" width="105" height="12" />
        <rect x="163" y="11" rx="4" ry="4" width="64" height="29" />
        <!-- Prev + page + next -->
        <rect x="236" y="16" rx="4" ry="4" width="20" height="20" />
        <rect x="265" y="11" rx="4" ry="4" width="40" height="29" />
        <rect x="314" y="16" rx="4" ry="4" width="20" height="20" />
      </LoaderSkeleton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useResponsive } from '@cvo/ui/composables';
import LoaderSkeleton from '../../domain/LoaderSkeleton/LoaderSkeleton.vue';

interface ReceiptsSkeletonProps {
  /** Number of skeleton rows/cards to render while loading */
  rows?: number;
}

const props = withDefaults(defineProps<ReceiptsSkeletonProps>(), {
  rows: 6,
});

const { isTabletOrDesktop } = useResponsive();

const VIEW_WIDTH = 800;
const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 37;
const HEADER_LABEL_Y = 14;
const LABEL_HEIGHT = 12;
const CELL_HEIGHT = 12;
const CELL_Y_OFFSET = 13;
const BADGE_Y_OFFSET = 8;
const STATUS_X = 720;
const STATUS_LABEL_WIDTH = 40;
const STATUS_BADGE_WIDTH = 64;
const STATUS_BADGE_HEIGHT = 22;

const PAGINATION_HEIGHT = 52;
const PAGINATION_HALF_HEIGHT = 26;
const PAGINATION_LABEL_X = 494;
const PAGINATION_DROPDOWN_X = 612;
const PAGINATION_PREV_X = 693;
const PAGINATION_PAGE_X = 724;
const PAGINATION_NEXT_X = 775;

const CARD_VIEW_BOX = '0 0 400 216';

// Column x-offsets and widths over the 800-unit viewBox, mirroring the
// DataTable order: date, description, receiptNumber, servicePeriod, amount.
const COLUMNS = [
  { x: 0,   labelWidth: 40,  cellWidth: 56 },
  { x: 110, labelWidth: 90,  cellWidth: 150 },
  { x: 300, labelWidth: 90,  cellWidth: 110 },
  { x: 450, labelWidth: 100, cellWidth: 150 },
  { x: 640, labelWidth: 50,  cellWidth: 50 },
];

const rowsLayout = computed(() =>
  Array.from({ length: props.rows }, (_, index) => {
    const top = HEADER_HEIGHT + index * ROW_HEIGHT;
    return {
      index,
      barY: top + CELL_Y_OFFSET,
      badgeY: top + BADGE_Y_OFFSET,
    };
  }));

const tableHeight = computed(() => HEADER_HEIGHT + props.rows * ROW_HEIGHT + PAGINATION_HEIGHT);
const paginationCenterY = computed(() => HEADER_HEIGHT + props.rows * ROW_HEIGHT + PAGINATION_HALF_HEIGHT);
const tableViewBox = computed(() => `0 0 ${VIEW_WIDTH} ${tableHeight.value}`);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .ReceiptsSkeleton {
    width: 100%;

    &-table {
      width: 100%;
    }

    &-cards {
      display: flex;
      flex-direction: column;
    }

    &-card {
      border-bottom: vars.$organisms-table-row-border-stroke solid vars.$organisms-table-row-border-color;
      padding: vars.$dimensions-d-4 0;
    }
  }
}
</style>
